'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, AlertCircle, Plus, Trash2 } from 'lucide-react';
import type { Transaction, TransactionFormValues } from './CashFlowSheetClient';

const TIPOS_STORAGE_KEY = 'cashflow_tipos_operacao';

const DEFAULT_TIPOS = ['Recibo', 'Fatura', 'Transferência', 'Cheque', 'Numerário', 'Outro'];

function loadTipos(): string[] {
  if (typeof window === 'undefined') return DEFAULT_TIPOS;
  try {
    const raw = localStorage.getItem(TIPOS_STORAGE_KEY);
    if (raw) return JSON.parse(raw) as string[];
  } catch {
    // ignore
  }
  return DEFAULT_TIPOS;
}

function saveTipos(tipos: string[]) {
  try {
    localStorage.setItem(TIPOS_STORAGE_KEY, JSON.stringify(tipos));
  } catch {
    // ignore
  }
}

interface TransactionModalProps {
  transaction: Transaction | null;
  onClose: () => void;
  onSubmit: (values: TransactionFormValues) => void;
}

export default function TransactionModal({
  transaction,
  onClose,
  onSubmit,
}: TransactionModalProps) {
  const isEdit = !!transaction;
  const firstInputRef = useRef<HTMLInputElement>(null);

  const [tipos, setTipos] = useState<string[]>(() => loadTipos());
  const [showManageTipos, setShowManageTipos] = useState(false);
  const [newTipo, setNewTipo] = useState('');
  const [tipoError, setTipoError] = useState('');
  const [tipoAdded, setTipoAdded] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<TransactionFormValues>({
    defaultValues: {
      data: '',
      descricao: '',
      tipo: 'entrada',
      valor: '',
      tipoOperacao: '',
      numero: '',
    },
  });

  const tipoWatched = watch('tipo');

  // Populate form when editing
  useEffect(() => {
    if (transaction) {
      reset({
        data: transaction.data,
        descricao: transaction.descricao,
        tipo: transaction.tipo,
        valor: transaction.valor.toFixed(2).replace('.', ','),
        tipoOperacao: transaction.tipoOperacao ?? '',
        numero: transaction.numero ?? '',
      });
    } else {
      const today = new Date();
      const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      reset({ data: iso, descricao: '', tipo: 'entrada', valor: '', tipoOperacao: '', numero: '' });
    }
  }, [transaction, reset]);

  // Focus first input on open
  useEffect(() => {
    const t = setTimeout(() => firstInputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const doSubmit = (values: TransactionFormValues) => {
    const cleaned = values.valor.replace(/\./g, '').replace(',', '.');
    const normalized = { ...values, valor: cleaned };
    onSubmit({ ...normalized, valor: normalized.valor });
  };

  const handleAddTipo = () => {
    const trimmed = newTipo.trim();
    if (!trimmed) {
      setTipoError('Escreva um nome para o novo tipo.');
      return;
    }
    if (tipos.map((t) => t.toLowerCase()).includes(trimmed.toLowerCase())) {
      setTipoError('Este tipo já existe.');
      return;
    }
    const updated = [...tipos, trimmed];
    setTipos(updated);
    saveTipos(updated);
    setNewTipo('');
    setTipoError('');
    setTipoAdded(true);
    setTimeout(() => setTipoAdded(false), 1500);
  };

  const handleRemoveTipo = (tipo: string) => {
    const updated = tipos.filter((t) => t !== tipo);
    setTipos(updated);
    saveTipos(updated);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-enter bg-card rounded-xl shadow-modal w-full max-w-md border border-border max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10">
          <h2 id="modal-title" className="text-base font-semibold text-foreground">
            {isEdit ? 'Editar Lançamento' : 'Novo Lançamento'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 active:scale-90"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(doSubmit)} noValidate>
          <div className="px-5 py-5 flex flex-col gap-4">

            {/* Tipo de Lançamento (entrada/saida) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Tipo de Lançamento <span className="text-danger">*</span>
              </label>
              <div className="flex gap-2">
                {(['entrada', 'saida'] as const).map((tipo) => (
                  <button
                    key={`tipo-${tipo}`}
                    type="button"
                    onClick={() => setValue('tipo', tipo, { shouldValidate: true })}
                    className={`
                      flex-1 py-2 rounded-md border text-sm font-medium transition-all duration-150
                      ${
                        tipoWatched === tipo
                          ? tipo === 'entrada' ?'bg-entrada-soft border-success text-entrada' :'bg-saida-soft border-danger text-saida' :'bg-muted border-border text-muted-foreground hover:border-foreground/30'
                      }
                    `}
                  >
                    {tipo === 'entrada' ? '↑ Entrada' : '↓ Saída'}
                  </button>
                ))}
              </div>
              <input
                type="hidden"
                {...register('tipo', { required: 'Selecione o tipo de lançamento' })}
              />
              {errors.tipo && (
                <p className="mt-1 text-xs text-danger flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.tipo.message}
                </p>
              )}
            </div>

            {/* Tipo de Operação (dropdown) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="modal-tipo-operacao" className="block text-sm font-medium text-foreground">
                  Tipo
                </label>
                <button
                  type="button"
                  onClick={() => setShowManageTipos((v) => !v)}
                  className="text-xs text-primary hover:underline"
                >
                  {showManageTipos ? 'Fechar gestão' : 'Gerir tipos'}
                </button>
              </div>

              {showManageTipos && (
                <div className="mb-2 p-3 rounded-md border border-border bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-2 font-medium">Gerir tipos de operação</p>
                  <div className="flex flex-col gap-1 mb-2 max-h-36 overflow-y-auto">
                    {tipos.map((t) => (
                      <div key={t} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-foreground">{t}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveTipo(t)}
                          className="text-muted-foreground hover:text-danger transition-colors"
                          title="Remover tipo"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTipo}
                      onChange={(e) => { setNewTipo(e.target.value); setTipoError(''); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddTipo();
                        }
                      }}
                      placeholder="Novo tipo…"
                      className="flex-1 px-2 py-1.5 rounded-md border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring"
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); handleAddTipo(); }}
                      className="px-2 py-1.5 rounded-md bg-primary text-primary-foreground text-sm hover:opacity-90 transition-all"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  {tipoError && (
                    <p className="mt-1 text-xs text-danger flex items-center gap-1">
                      <AlertCircle size={12} /> {tipoError}
                    </p>
                  )}
                  {tipoAdded && (
                    <p className="mt-1 text-xs text-success flex items-center gap-1">
                      ✓ Tipo adicionado com sucesso.
                    </p>
                  )}
                </div>
              )}

              <select
                id="modal-tipo-operacao"
                {...register('tipoOperacao')}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-colors duration-150"
              >
                <option value="">— Selecionar tipo —</option>
                {tipos.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Número */}
            <div>
              <label htmlFor="modal-numero" className="block text-sm font-medium text-foreground mb-1">
                Número
              </label>
              <input
                id="modal-numero"
                type="text"
                placeholder="Ex: 1043"
                {...register('numero')}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring transition-colors duration-150"
              />
            </div>

            {/* Data */}
            <div>
              <label htmlFor="modal-data" className="block text-sm font-medium text-foreground mb-1">
                Data <span className="text-danger">*</span>
              </label>
              <input
                id="modal-data"
                type="date"
                ref={firstInputRef}
                {...register('data', { required: 'Informe a data do lançamento' })}
                className={`
                  w-full px-3 py-2 rounded-md border bg-background text-sm text-foreground
                  focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring
                  transition-colors duration-150
                  ${errors.data ? 'border-danger' : 'border-input'}
                `}
              />
              {errors.data && (
                <p className="mt-1 text-xs text-danger flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.data.message}
                </p>
              )}
            </div>

            {/* Descrição */}
            <div>
              <label htmlFor="modal-desc" className="block text-sm font-medium text-foreground mb-1">
                Descrição <span className="text-danger">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">
                Descreva brevemente a movimentação (ex: pagamento cliente, aluguel)
              </p>
              <input
                id="modal-desc"
                type="text"
                placeholder="Ex: Pagamento cliente — NF 1043"
                {...register('descricao', {
                  required: 'Informe a descrição do lançamento',
                  maxLength: { value: 120, message: 'Máximo de 120 caracteres' },
                })}
                className={`
                  w-full px-3 py-2 rounded-md border bg-background text-sm text-foreground
                  placeholder:text-muted-foreground
                  focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring
                  transition-colors duration-150
                  ${errors.descricao ? 'border-danger' : 'border-input'}
                `}
              />
              {errors.descricao && (
                <p className="mt-1 text-xs text-danger flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.descricao.message}
                </p>
              )}
            </div>

            {/* Valor */}
            <div>
              <label htmlFor="modal-valor" className="block text-sm font-medium text-foreground mb-1">
                Valor <span className="text-danger">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">
                Use vírgula como separador decimal (ex: 1.500,00)
              </p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-mono-nums">
                  €
                </span>
                <input
                  id="modal-valor"
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  {...register('valor', {
                    required: 'Informe o valor do lançamento',
                    validate: (v) => {
                      const n = parseFloat(v.replace(',', '.'));
                      if (isNaN(n) || n <= 0) return 'Informe um valor válido maior que zero';
                      return true;
                    },
                  })}
                  className={`
                    w-full pl-9 pr-3 py-2 rounded-md border bg-background text-sm text-foreground font-mono-nums
                    placeholder:text-muted-foreground
                    focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring
                    transition-colors duration-150
                    ${errors.valor ? 'border-danger' : 'border-input'}
                  `}
                />
              </div>
              {errors.valor && (
                <p className="mt-1 text-xs text-danger flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.valor.message}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2 sticky bottom-0 bg-card">
            <button
              type="button"
              onClick={onClose}
              className="
                px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground
                hover:bg-muted transition-all duration-150 active:scale-95
              "
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="
                min-w-[120px] px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium
                hover:opacity-90 transition-all duration-150 active:scale-95
                disabled:opacity-60 disabled:cursor-not-allowed
                flex items-center justify-center gap-2
              "
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Salvando…
                </>
              ) : isEdit ? (
                'Salvar Alterações'
              ) : (
                'Adicionar Lançamento'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}